import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Task } from "../models/Task";
import { TaskService } from "../services/TaskService";
import { useAuth } from "./AuthContext";
import { useMatter } from "./MatterContext";
import { MatterService } from "../services/MatterService";
import { useSocketEventByIds } from "../hooks/useSocketEventByIds";
import { Subtask } from "../models/Subtask";

interface Errors {
  list?: Error | null;
  details?: Error | null;
  subtasks?: Error | null;
}
interface Loadings {
  list?: boolean;
  details?: boolean;
  subtasks?: boolean;
}

interface TaskContextProps {
  tasks: Task[];
  taskCounts: Record<Task["type"], number>;
  selectedTask: Task | null;
  subtasks: Subtask[];
  setSelectedTask: (taskId: string) => void;
  createTask: (
    taskData: Omit<
      Task,
      "_id" | "createdAt" | "updatedAt" | "createdBy" | "matter"
    >,
  ) => Promise<string>;
  updateTask: (
    taskId: string,
    updatedData: Partial<Omit<Task, "_id" | "createdAt" | "updatedAt">>,
  ) => Promise<void>;
  deleteTaskById: (taskId: string) => void;
  loading: Loadings;
  error: Errors;
}

const TaskContext = createContext<TaskContextProps | undefined>(undefined);

export const useTask = (): TaskContextProps => {
  const context = useContext(TaskContext);
  if (!context) {
    throw new Error("useTask must be used within a TaskProvider");
  }
  return context;
};

export const TaskProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { currentMatterId } = useMatter();
  const { user } = useAuth();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [taskCounts, setTaskCounts] = useState<Record<Task["type"], number>>({
    event: 0,
    document: 0,
    general: 0,
    evidence: 0,
    discovery: 0,
  });
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [subtasks, setSubTasks] = useState<Subtask[]>([]);
  const [errors, setErrors] = useState<Errors>({});
  const [loadings, setLoadings] = useState<Loadings>({
    list: true,
    details: true,
  });

  const fetchTasks = async () => {
    if (!user || !currentMatterId) return;
    try {
      const fetchedTasks = await MatterService.getTasks(currentMatterId);
      setTasks(fetchedTasks);
      setLoadings((prev) => ({ ...prev, list: false }));
    } catch (err: any) {
      setErrors((prev) => ({ ...prev, list: err }));
      setLoadings((prev) => ({ ...prev, list: false }));
    }
  };

  const fetchSubtasks = async (taskId: string) => {
    if (!user || !currentMatterId) return;
    setLoadings((prev) => ({ ...prev, subtasks: true }));
    try {
      const subtasks = await TaskService.getSubtasks(currentMatterId, taskId);
      setSubTasks(subtasks);
      setLoadings((prev) => ({ ...prev, subtasks: false }));
    } catch (err: any) {
      setErrors((prev) => ({ ...prev, subtasks: err }));
      setLoadings((prev) => ({ ...prev, subtasks: false }));
    }
  };

  // Fetch all tasks
  useEffect(() => {
    setLoadings((prev) => ({ ...prev, list: true }));
    fetchTasks();
  }, [user, currentMatterId]);

  useSocketEventByIds("tasks:changed", ["*"], () => {
    fetchTasks();
  });

  useSocketEventByIds("subtasks:changed", ["*"], (document: Subtask) => {
    if (selectedTask && document.task === selectedTask._id) {
      subtasks.push(document);
    }
  });

  // Count incomplete tasks by type
  useEffect(() => {
    const counts = tasks
      .filter((task) => !task.isCompleted)
      .reduce(
        (acc, task) => {
          acc[task.type] = (acc[task.type] || 0) + 1;
          return acc;
        },
        { event: 0, document: 0, general: 0, evidence: 0, discovery: 0 },
      );
    setTaskCounts(counts);
  }, [tasks]);

  const setSelectedTaskById = async (taskId: string) => {
    if (!currentMatterId) return;
    try {
      setLoadings((prev) => ({ ...prev, details: true }));
      const taskData = await TaskService.getTask(currentMatterId, taskId);
      setSelectedTask(taskData);
      setLoadings((prev) => ({ ...prev, details: false }));
      fetchSubtasks(taskId);
    } catch (err: any) {
      setErrors((prev) => ({ ...prev, details: err }));
      setLoadings((prev) => ({ ...prev, details: false }));
    }
  };

  const createTask = async (
    taskData: Omit<
      Task,
      "_id" | "createdAt" | "updatedAt" | "createdBy" | "matter" | "updatedBy"
    >,
  ): Promise<string> => {
    const newTaskId = await TaskService.createTask(currentMatterId!, taskData);
    return newTaskId;
  };

  const updateTask = async (
    taskId: string,
    updatedData: Partial<Omit<Task, "_id" | "createdAt" | "updatedAt">>,
  ) => {
    if (!currentMatterId) return;
    await TaskService.updateTask(currentMatterId, taskId, updatedData);
    await setSelectedTaskById(taskId);
  };

  const deleteTaskById = async (taskId: string) => {
    if (!currentMatterId) return;
    await TaskService.deleteTask(currentMatterId, taskId);
    setTasks((prev) => prev.filter((t) => t._id !== taskId));
    setSelectedTask(null);
  };

  return (
    <TaskContext.Provider
      value={{
        taskCounts,
        loading: loadings,
        error: errors,
        tasks,
        selectedTask,
        subtasks,
        createTask,
        updateTask,
        deleteTaskById,
        setSelectedTask: setSelectedTaskById,
      }}
    >
      {children}
    </TaskContext.Provider>
  );
};
